import _ from 'lodash'
import EventBus from '@/components/mixins/event-bus-mixin'
import { Dependencies } from './utils'
import CONST from './constants'
import { t } from '@/i18n/i18ns'
import { useCognitoAuthStore } from '@base-shared/stores/cognito-auth'

const ACCESS_DENIED_EXCEPTION = 'AccessDeniedException'
const RETRY_REQUESTS_QUEUE = []

/**
 * This is the queue of callbacks which should happen
 * after 401 resolved via session refresh.
 */
EventBus.$on('USER_SESSION_UPDATED', async () => {
  let session
  try {
    session = await useCognitoAuthStore().fetchAndStoreAuthSession()
  } catch {
    // Continue and drain retry queue even when session refresh is unavailable.
    session = null
  }

  const token = session?.tokens?.idToken?.toString()
  if (!token) {
    // No refreshed session/token available: do not retry queued requests.
    RETRY_REQUESTS_QUEUE.length = 0
    return
  }

  while (RETRY_REQUESTS_QUEUE.length > 0) {
    const [config, callback] = RETRY_REQUESTS_QUEUE.pop()
    if (config) {
      config.headers = config.headers ?? {}
      config.headers.Authorization = token
    }
    callback()
  }
})

export default {
  handleUnauthorizedError (error, params) {
    if (!error) error = {}
    if (!params) params = {}

    if ((error.response) && (error.response.status && error.response.status === CONST.UNAUTHORIZED_CODE)) {
      if (params.originalRequest && (typeof params.originalRequest === 'function')) {
        RETRY_REQUESTS_QUEUE.push([error.config, params.originalRequest])
      }
      EventBus.$emit('REFRESH_USER_SESSION')
    }
  },
  handleAPIRequestNormalError (error = {}, params = {}) {
    // NOTE: Show "Something went wrong" pop-up and redurect to homepage:
    // the best we could do in case of 403 error. DO NOT attempt to Retry 403, it shouldn't help

    // Instead of catching all of 403 errors, we catch only AccessDeniedException
    // and redirect to homepage.
    const exceptionName = error?.response?.data?.name ?? error?.response?.data?.exception;

    const isForbiddenError = error.response?.status === CONST.FORBIDDEN_CODE;
    const isAccessDeniedException = exceptionName === ACCESS_DENIED_EXCEPTION;

    if (isForbiddenError && isAccessDeniedException) {
      if ($('#customNotification').hasClass('is-visible')) {
        console.log('Something went wrong with the HTTP request: ', error.response)
        return
      }

      const router = Dependencies.inject('$routerInstance')
      const redirectToHome = () => {
        if (router?.default?.app?.$route?.path === '/menu') {
          return
        }
        router.push('/menu', () => {})
      }

      const customNotificaionParams = {
        type: 'error',
        alert: {
          message: t('label.forbidden')
        },
        closeButtonText: t('buttons.ok'),
        callbacks: {
          eventOnClose: redirectToHome,
        }
      }

      EventBus.$emit('SHOW_CUSTOM_NOTIFICATION', customNotificaionParams)
    } else
      if (error.response && (error.response.status && error.response.status !== CONST.UNAUTHORIZED_CODE)) {
        // Process Callback
        if (_.isFunction(params.callback)) {
          params.callback(null)
        }

        // Show Dialog
        if (params.showDialogNotification) {
          let message = ''

          // Error message from response by default
          if (error.response && error.response.data) {
            message = (error.response.data.description) ? error.response.data.description : error.response.data.name

            if (!message && error.response.data.message) {
              message = error.response.data.message
            }
          }

          // Set message if dialogErrorMessage from params is present
          if (params.dialogErrorMessage) {
            message = params.dialogErrorMessage
          }

          const customNotificaionParams = {
            type: 'error',
            alert: {
              message
            }
          }

          if (params.dialogEventOnClose) {
            customNotificaionParams.callbacks = { eventOnClose: params.dialogEventOnClose }
          }

          EventBus.$emit('SHOW_CUSTOM_NOTIFICATION', customNotificaionParams)
        }
      }
  },
  handleAPIRequestError (error, params) {
    if (!error) error = {}
    if (!params) params = {}

    // Unauthorized  error
    if ((error.response && error.response.status) && (error.response.status === CONST.UNAUTHORIZED_CODE)) {
      this.handleUnauthorizedError(error, params)
    }

    // Normal Error
    if ((error.response && error.response.status) && (error.response.status !== CONST.UNAUTHORIZED_CODE)) {
      this.handleAPIRequestNormalError(error, params)
    }
  }
}
